import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ConfigService } from './config.service';

type CsrfTokenResponse = {
  token?: string;
};

export const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';

@Injectable({
  providedIn: 'root'
})
export class CsrfService {
  private readonly http: HttpClient;
  private readonly configReadyPromise: Promise<void>;
  private baseUrl = '';
  private refreshPromise: Promise<string | null> | null = null;
  public readonly $token = new BehaviorSubject<string | null>(null);

  constructor(httpBackend: HttpBackend, private readonly configService: ConfigService) {
    this.http = new HttpClient(httpBackend);
    this.configReadyPromise = this.configService.$configuration.pipe(
      filter(cfg => !!cfg && !!cfg.servicesUrl),
      take(1)
    )
      .toPromise()
      .then(cfg => {
        this.baseUrl = cfg.servicesUrl;
      });

    this.configService.$configuration.subscribe(cfg => {
      this.baseUrl = cfg?.servicesUrl ?? '';
    });
  }

  public get token(): string | null {
    return this.$token.value;
  }

  public clearToken(): void {
    this.$token.next(null);
  }

  public async ensureToken(forceRefresh = false): Promise<string | null> {
    if (!forceRefresh && this.token) {
      return this.token;
    }

    if (!forceRefresh && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.fetchToken().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  public async refreshToken(): Promise<string | null> {
    return this.ensureToken(true);
  }

  public isApiRequest(url: string): boolean {
    try {
      const requestUrl = new URL(url, window.location.origin);
      const apiBaseUrl = new URL(this.baseUrl || window.location.origin, window.location.origin);
      const apiPathPrefix = `${apiBaseUrl.pathname.replace(/\/$/, '')}/api/`;

      return requestUrl.origin === apiBaseUrl.origin && requestUrl.pathname.startsWith(apiPathPrefix);
    } catch {
      return false;
    }
  }

  private async fetchToken(): Promise<string | null> {
    await this.configReadyPromise;

    try {
      const response = await firstValueFrom(this.http.get<CsrfTokenResponse>(`${this.baseUrl}/api/auth/csrf-token`, {
        withCredentials: true
      }));
      const token = response?.token ?? null;
      this.$token.next(token);
      return token;
    } catch {
      this.$token.next(null);
      return null;
    }
  }
}